"use client"

import { useTranslations } from "next-intl"
import { useState, useMemo, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { DialogCard } from "@/components/ui/dialog-card"
import { DockSheet } from "@/components/ui/dock-sheet"
import { SHEET_Z_GLOBAL } from "@/lib/sheet-styles"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { X, CheckSquare, Square, ChevronDown, ChevronUp, Info, AlertCircle } from "lucide-react"
import { useMediaQuery } from "@/hooks/use-media-query"
import SearchableMultiDropdown from "@/components/ui/SearchableMultiDropdown"
import { transformPayPalCountriesToDropdownOptions, getCountryName, type PayPalCountry } from "@/lib/paypal-countries"
import type { Option } from "@/components/ui/SearchableDropdown"
import { MobileFriendlyTooltip } from "@/components/ui/mobile-friendly-tooltip"
import {
  creationFlowInputClass,
  creationFlowLabelClass,
  creationFlowTextareaClass,
  creationFlowBlockClass,
  creationFlowPrimaryCtaClass,
  creationFlowButtonOutlineClass,
  creationFlowMutedClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { getCurrencySymbol, isZeroDecimalCurrency } from "@/lib/currencies"

type NewShippingMethod = {
  name: string
  description: string
  price: string
  bannedCountries: string[]
}

interface ShippingMethodDialogProps {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  newShippingMethod: NewShippingMethod
  setNewShippingMethodAction: React.Dispatch<React.SetStateAction<NewShippingMethod>>
  onAddMethodAction: () => void
  onUpdateMethod?: (id: string) => void
  error: string
  baseCountry?: string
  isEditing?: boolean
  editingId?: string
  currencyCode?: string
}

export function ShippingMethodDialog({
  open,
  onOpenChangeAction,
  newShippingMethod,
  setNewShippingMethodAction,
  onAddMethodAction,
  onUpdateMethod,
  error,
  baseCountry = "US",
  isEditing = false,
  editingId,
  currencyCode = "USD",
}: ShippingMethodDialogProps) {
  const t = useTranslations("shippingSettings");
  const tCommon = useTranslations("common");
  const [mounted, setMounted] = useState(false)
  const isMobile = useMediaQuery("(max-width: 768px)")
  const [showAllBadges, setShowAllBadges] = useState(false)
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false)
  const currencySymbol = getCurrencySymbol(currencyCode)
  const zeroDecimalCurrency = isZeroDecimalCurrency(currencyCode)
  const bannedCountries = Array.isArray(newShippingMethod.bannedCountries) ? newShippingMethod.bannedCountries : []

  useEffect(() => {
    setMounted(true)
  }, [])

  const paypalCountryOptions = useMemo(() => {
    return transformPayPalCountriesToDropdownOptions()
  }, [])

  const baseCountryObject = useMemo(() => {
    return paypalCountryOptions.find((c) => c.code === baseCountry)
  }, [baseCountry, paypalCountryOptions])

  const totalCountries = paypalCountryOptions.length

  const banAllCodes = useMemo(() => {
    return paypalCountryOptions.filter((country) => country.code !== baseCountry).map((country) => country.code)
  }, [paypalCountryOptions, baseCountry])

  const isBanAllActive = useMemo(() => {
    if (bannedCountries.length !== banAllCodes.length) return false
    return banAllCodes.every((code) => bannedCountries.includes(code))
  }, [bannedCountries, banAllCodes])

  const everyCountryBanned = bannedCountries.length >= totalCountries

  const bannedCountryObjects = useMemo(() => {
    return bannedCountries
      .map((code) => paypalCountryOptions.find((c) => c.code === code))
      .filter(Boolean) as Option[]
  }, [bannedCountries, paypalCountryOptions])

  const displayedCountries = useMemo(() => {
    return showAllBadges ? bannedCountryObjects : bannedCountryObjects.slice(0, 5)
  }, [bannedCountryObjects, showAllBadges])

  const hasMoreCountries = bannedCountryObjects.length > 5
  const bannedCountriesCount = bannedCountryObjects.length

  const handleBannedCountriesChange = (codes: string[]) => {
    const unique = Array.from(new Set(codes))
    if (unique.length >= totalCountries) return
    setNewShippingMethodAction({
      ...newShippingMethod,
      bannedCountries: unique,
    })
  }

  const handleRemoveBannedCountry = (countryCode: string) => {
    setNewShippingMethodAction({
      ...newShippingMethod,
      bannedCountries: bannedCountries.filter((code) => code !== countryCode),
    })
  }

  const toggleAllCountries = () => {
    if (isBanAllActive) {
      setNewShippingMethodAction({
        ...newShippingMethod,
        bannedCountries: [],
      })
    } else {
      setNewShippingMethodAction({
        ...newShippingMethod,
        bannedCountries: banAllCodes,
      })
    }
  }

  const nameStr = typeof newShippingMethod.name === "string" ? newShippingMethod.name : String(newShippingMethod.name ?? "")
  const priceStr = typeof newShippingMethod.price === "string" ? newShippingMethod.price : String(newShippingMethod.price ?? "")
  const descriptionStr = typeof newShippingMethod.description === "string" ? newShippingMethod.description : String(newShippingMethod.description ?? "")
  const isFormValid = nameStr.trim() !== "" && priceStr.trim() !== ""

  const handleSubmit = () => {
    if (isEditing && editingId && onUpdateMethod) {
      onUpdateMethod(editingId)
    } else {
      onAddMethodAction()
    }
  }

  /* The former `dark` prop + `formDark = dark || !isMobile` are gone. The
     boolean was never two-valued: two call sites passed `dark` as a literal
     `true`, the third passed `isMobile`, and `isMobile || !isMobile` is `true`.
     Both mount points below are overlays (DockSheet / DialogCard), so this form
     always spoke the creation-flow on-panel dialect and the page dialect was
     dead code. Removed rather than renamed.
     @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md */
  const inputClass = creationFlowInputClass
  const labelClass = creationFlowLabelClass
  const textareaClass = "min-h-[80px] " + creationFlowTextareaClass
  const priceInputClass = creationFlowInputClass
  const priceCurrencySpanClass = "text-lg mr-2 text-foreground"

  const renderForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name" className={labelClass}>{t("methodName")}</Label>
        <Input
          id="name"
          placeholder={t("namePlaceholder")}
          value={nameStr}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNewShippingMethodAction({
              ...newShippingMethod,
              name: e.target.value,
            })
          }
          className={inputClass}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description" className={labelClass}>
          Description 
          <span className="font-normal ml-1 text-muted-foreground">({t("optional")})</span>
        </Label>
        <Textarea
          id="description"
          placeholder={t("descPlaceholder")}
          value={descriptionStr}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setNewShippingMethodAction({
              ...newShippingMethod,
              description: e.target.value,
            })
          }
          className={textareaClass}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="price" className={labelClass}>{t("price")}</Label>
        <div className="flex items-center">
          <span className={priceCurrencySpanClass}>{currencySymbol}</span>
          <Input
            id="price"
            type="text"
            inputMode={zeroDecimalCurrency ? "numeric" : "decimal"}
            pattern={zeroDecimalCurrency ? "[0-9]*" : "[0-9]*\\.?[0-9]*"}
            placeholder={zeroDecimalCurrency ? "0" : "0.00"}
            value={priceStr}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              const regex = zeroDecimalCurrency ? /^[0-9]*$/ : /^[0-9]*\.?[0-9]*$/
              if (value === "" || regex.test(value)) {
                setNewShippingMethodAction({
                  ...newShippingMethod,
                  price: value,
                })
              }
            }}
            className={priceInputClass}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className={labelClass}>{t("bannedCountries")}</Label>
          <MobileFriendlyTooltip
            content={t("bannedTooltip", { country: baseCountryObject?.name || baseCountry })}
            side={isMobile ? "top" : "bottom"}
            align={isMobile ? "center" : "start"}
            className="max-w-[280px]"
          >
            <button type="button" className="text-muted-foreground hover:text-foreground">
              <Info className="h-4 w-4" />
            </button>
          </MobileFriendlyTooltip>
        </div>

        {isBanAllActive && !everyCountryBanned && (
          <div className="rounded-2xl p-3 flex items-start gap-2 bg-muted/80 border border-sheet-handle">
            <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground">{t("allBanned")}</p>
              <p className={creationFlowMutedClass}>
                {t("allBannedDesc", { country: baseCountryObject?.name || baseCountry })}
              </p>
            </div>
          </div>
        )}

        {everyCountryBanned && (
          <div className="rounded-2xl p-3 flex items-start gap-2 bg-destructive/20 border border-destructive/30">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-destructive text-sm font-medium">{t("errAtLeastOne")}</p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggleAllCountries}
            className={cn("h-10 px-3 flex items-center gap-2 rounded-xl text-sm", creationFlowButtonOutlineClass)}
          >
            {isBanAllActive ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            <span>{isBanAllActive ? t("unbanAll") : t("banAll")}</span>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <SearchableMultiDropdown
            options={paypalCountryOptions}
            label="name"
            selectedCodes={bannedCountries}
            onChange={handleBannedCountriesChange}
            placeholder={t("selectCountries")}
            sheetTitle={t("selectCountries")}
            className="flex-1"
            showCode={false}
            onOpenChange={setIsCountryDropdownOpen}
            selectAllLabel={t("banAll")}
            deselectAllLabel={t("unbanAll")}
            selectAllExcludeCodes={[baseCountry]}
            selectedCountLabel={(count) => `${count}/${totalCountries}`}
          />
          {bannedCountriesCount > 0 && (
            <Badge
              variant="secondary"
              className="px-2 py-1 bg-sheet-handle text-foreground border-0"
            >
              {bannedCountriesCount}/{totalCountries}
            </Badge>
          )}
        </div>

        {bannedCountriesCount > 0 && (
          <div className="rounded-2xl bg-muted p-4">
            <div
              className={cn(
                "flex flex-wrap gap-2",
                showAllBadges ? "max-h-60 overflow-y-auto pr-2" : "max-h-[120px] overflow-y-auto pr-2"
              )}
            >
              {displayedCountries.map((country) => (
                <Badge
                  key={country.isoCode}
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-1 border-0 bg-sheet-handle text-foreground"
                >
                  <span className="text-base leading-none">{country.flag}</span>
                  <span className="mx-1">{country.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 text-foreground/80 hover:text-foreground hover:bg-foreground/20"
                    onClick={() => handleRemoveBannedCountry(country.code)}
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Remove</span>
                  </Button>
                </Badge>
              ))}
            </div>
            <div className="mt-2 flex justify-center">
              {hasMoreCountries && !showAllBadges && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn("text-xs flex items-center gap-1", creationFlowMutedClass, "hover:text-foreground")}
                  onClick={() => setShowAllBadges(true)}
                >
                  <span>{t("showMore", { count: bannedCountryObjects.length - 5 })}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              )}
              {showAllBadges && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn("text-xs flex items-center gap-1", creationFlowMutedClass, "hover:text-foreground")}
                  onClick={() => setShowAllBadges(false)}
                >
                  <span>{tCommon("showLess")}</span>
                  <ChevronUp className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-2xl p-3 flex items-start gap-2 bg-destructive/20 border border-destructive/30">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive text-sm font-medium">{error}</p>
        </div>
      )}
    </div>
  )

  if (!mounted) return null

  const mobileView = (
    <DockSheet
      open={open}
      onOpenChange={onOpenChangeAction}
      title={isEditing ? t("editMethod") : t("addMethod")}
      footer={
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid}
          className={creationFlowPrimaryCtaClass}
        >
          {isEditing ? t("updateMethod") : t("addMethod")}
        </Button>
      }
      zIndex={SHEET_Z_GLOBAL}
    >
      <>
        <p className="text-sm text-muted-foreground pb-4">
          {t("subtitle")}
        </p>
        {renderForm()}
      </>
    </DockSheet>
  )

  const desktopTitle = isEditing ? t("editMethod") : t("addMethod")
  const desktopView = (
    /* `nested` — this card opens ON TOP of the shipping step's own dialog, so
       it takes the `*_NESTED` overlay + panel z-layer (HANDOFF §7). */
    <DialogCard
      open={open}
      onOpenChange={onOpenChangeAction}
      nested
      title={desktopTitle}
      belowTitle={<p className="text-muted-foreground text-sm text-center">{t("subtitle")}</p>}
      panelClassName="max-w-2xl h-[80dvh] max-h-[80dvh]"
      contentClassName="px-6"
      footer={
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid}
          className={creationFlowPrimaryCtaClass}
        >
          {isEditing ? t("updateMethod") : t("addMethod")}
        </Button>
      }
    >
      {renderForm()}
    </DialogCard>
  )

  return (
    <>
      {isMobile ? mobileView : desktopView}
    </>
  )
}
